import { useEffect, useState, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router';
import { ARTIFACT_TYPES, ARTIFACT_TYPE_LABELS, CLASSES, PROFESSIONS } from '@thottbot/shared';
import { useClassIcons } from '../api/client';
import { ClassIcon } from './ClassIcon';
import { UserBox } from './UserBox';

/** Below this width the menu is always expanded and the toggle/hot strip are hidden (CSS). */
export const PHONE_QUERY = '(max-width: 480px)';

const SECONDARY_SKILLS = ['Cooking', 'First Aid', 'Fishing'] as const;
const FIND_TYPES = ARTIFACT_TYPES.filter((t) => t !== 'ability');
const PRIMARY_PROFESSIONS = PROFESSIONS.filter(
  (p) => !(SECONDARY_SKILLS as readonly string[]).includes(p),
);

/** True while the viewport matches the phone breakpoint; false where matchMedia is unavailable. */
export function usePhoneWidth(): boolean {
  const [phone, setPhone] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(PHONE_QUERY).matches
      : false,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(PHONE_QUERY);
    const update = () => setPhone(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);
  return phone;
}

function Column({ title, children }: { title: string; children: ReactNode }) {
  return (
    <>
      <div className="menutitle">{title}</div>
      <div className="menuitems">{children}</div>
    </>
  );
}

/**
 * The 2009 landing menu: a "Menu" disclosure toggle (rendered in a row with `searchBox`),
 * any `children` between that row and the menu, then the four-column nav. `open` is owned by
 * the page (hover, click-to-pin, Escape); at phone width the nav is expanded regardless.
 */
export function SiteMenu({
  open,
  onToggle,
  onReveal,
  onLeave,
  toggleRef,
  searchBox,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  /** Pointer entered the toggle or the nav received focus: show the menu without pinning it. */
  onReveal?: () => void;
  /** Pointer left the region spanning the toggle row through the nav (2009's `onmouseout`). */
  onLeave?: () => void;
  toggleRef?: Ref<HTMLButtonElement>;
  searchBox?: ReactNode;
  children?: ReactNode;
}) {
  // Hooks first, unconditionally: `open || usePhoneWidth()` would skip the hook when open.
  const phone = usePhoneWidth();
  const classIcons = useClassIcons();
  const expanded = open || phone;
  return (
    <div
      className="menu-region"
      onPointerLeave={onLeave}
      // Keyboard counterpart of pointerleave: tabbing out of the region closes an unpinned menu.
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onLeave?.();
      }}
    >
      <div className="landing-search">
        <button
          type="button"
          className="menu-toggle"
          aria-expanded={expanded}
          aria-controls="site-menu"
          onClick={onToggle}
          onPointerEnter={onReveal}
          ref={toggleRef}
        >
          Menu
        </button>
        {searchBox}
      </div>
      {children}
      <nav id="site-menu" className="site-menu" aria-label="Menu" hidden={!expanded} onFocus={onReveal}>
        <div className="tmenu">
          <Column title="Find">
            {FIND_TYPES.map((t) => (
              <Link key={t} to={`/browse/${t}`}>
                {ARTIFACT_TYPE_LABELS[t].plural}
              </Link>
            ))}
            <Link to="/characters">Characters</Link>
          </Column>
        </div>
        <div className="tmenu">
          <Column title="Classes">
            {CLASSES.map((c) => (
              <Link key={c} to={`/browse/ability?class=${encodeURIComponent(c)}`}>
                <ClassIcon cls={c} url={classIcons.data?.[c]} />
                {c}
              </Link>
            ))}
          </Column>
        </div>
        <div className="tmenu">
          <Column title="Professions">
            {PRIMARY_PROFESSIONS.map((p) => (
              <Link key={p} to={`/browse/recipe?profession=${encodeURIComponent(p)}`}>
                {p}
              </Link>
            ))}
          </Column>
          <Column title="Secondary Skills">
            {SECONDARY_SKILLS.map((p) => (
              <Link key={p} to={`/browse/recipe?profession=${encodeURIComponent(p)}`}>
                {p}
              </Link>
            ))}
          </Column>
        </div>
        <div className="tmenu">
          <Column title="Misc">
            <Link to="/submit">Submit</Link>
            <Link to="/build">Build</Link>
            <Link to="/addon">Addon</Link>
            <Link to="/addon/watch">Auto-upload</Link>
            <UserBox variant="menu" />
          </Column>
        </div>
      </nav>
    </div>
  );
}
